import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  collection, 
  doc, 
  onSnapshot, 
  query, 
  orderBy, 
  where,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

interface Song {
  id: string;
  title: string;
  artist?: string;
  videoId: string;
  thumbnail: string;
  duration?: string;
  addedBy: string;
  addedByDeviceId?: string;
  singerName: string;
  addedAt: Date;
  status: 'queued' | 'ready' | 'playing' | 'played';
}

interface Party {
  id: string;
  name: string;
  createdBy: string;
  createdAt: Date;
  expiresAt?: Date;
  tier?: 'free' | 'premium';
  gameMode?: 'karaoke' | 'farkle' | 'imposter';
  settings: {
    maxQueueSize: number;
    allowDuplicates: boolean;
    requireAuth: boolean;
  };
  currentSong: Song | null;
  isActive: boolean;
}

interface PartyContextType {
  currentParty: Party | null;
  songQueue: Song[];
  currentSong: Song | null;
  loading: boolean;
  error: string | null;
  setCurrentParty: (party: Party | null) => void;
  isPartyOwner: boolean;
}

const PartyContext = createContext<PartyContextType | undefined>(undefined);

export const useParty = () => {
  const context = useContext(PartyContext);
  if (context === undefined) {
    throw new Error('useParty must be used within a PartyProvider');
  }
  return context;
};

export const PartyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [currentParty, setCurrentParty] = useState<Party | null>(null);
  const [songQueue, setSongQueue] = useState<Song[]>([]);
  const [currentSong, setCurrentSong] = useState<Song | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Toggle verbose firestore snapshot logging for debugging (set REACT_APP_VERBOSE_PARTY_LOGS=true)
  const VERBOSE_PARTY_LOGS = process.env.REACT_APP_VERBOSE_PARTY_LOGS === 'true';

  const isPartyOwner = user && currentParty ? user.uid === currentParty.createdBy : false;

  // Subscribe to party updates
  useEffect(() => {
    if (!currentParty) return;

    // Handle demo parties differently
    if (currentParty.id.startsWith('demo-')) {
      // For demo parties, just simulate some data
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'parties', currentParty.id),
      (doc) => {
        if (!doc.exists()) {
          setCurrentParty(null);
          return;
        }
        const data = doc.data();
        const updatedParty: Party = {
          ...data,
          id: doc.id,
          createdAt: data.createdAt?.toDate(),
          expiresAt: data.expiresAt?.toDate(),
          gameMode: data.gameMode || 'karaoke',
          currentSong: data.currentSong ? {
            ...data.currentSong,
            addedAt: data.currentSong.addedAt?.toDate()
          } : null
        } as Party;

        setCurrentParty(updatedParty);
        setCurrentSong(updatedParty.currentSong);
      },
      (error) => {
        setError('Failed to sync party data');
      }
    );

    return unsubscribe;
  }, [currentParty?.id]);

  // Subscribe to queue updates
  useEffect(() => {
    if (!currentParty) {
      setSongQueue([]);
      return;
    }

    // Handle demo parties differently
    if (currentParty.id.startsWith('demo-')) {
      setSongQueue([]);
      return;
    }

    const baseCol = collection(db, 'parties', currentParty.id, 'queue');
    let unsub: (() => void) | null = null;
    let fellBack = false;

    const startPrimaryListener = () => {
      const primaryQuery = query(
        baseCol,
        where('status', '==', 'queued'),
        orderBy('addedAt')
      );
      unsub = onSnapshot(
        primaryQuery,
        (snapshot) => {
          const songs = snapshot.docs.map(d => {
            const data = d.data() as any;
            const videoId = data.videoId || data.id; // keep videoId, not as list id
            return {
              ...data,
              videoId,
              id: d.id, // ensure Firestore doc id wins
              addedAt: data.addedAt?.toDate()
            } as Song;
          });
          setSongQueue(songs);
        },
        (error: any) => {
          // If index is still building or missing, fall back to orderBy-only and filter client-side
          const msg = String(error?.message || '').toLowerCase();
          if (!fellBack && (error?.code === 'failed-precondition' || msg.includes('requires an index'))) {
            fellBack = true;
            if (unsub) unsub();
            const fallbackQuery = query(baseCol, orderBy('addedAt'));
            unsub = onSnapshot(
              fallbackQuery,
              (snapshot) => {
                const songs = snapshot.docs
                  .map(d => {
                    const data = d.data() as any;
                    const videoId = data.videoId || data.id;
                    return {
                      ...data,
                      videoId,
                      id: d.id,
                      addedAt: data.addedAt?.toDate()
                    } as Song;
                  })
                  .filter((s: any) => s.status === 'queued') as Song[];
                setSongQueue(songs);
              },
              (fallbackErr) => {
                setError('Failed to sync queue data');
              }
            );
          } else {
            setError('Failed to sync queue data');
          }
        }
      );
    };

    startPrimaryListener();
    return () => { if (unsub) unsub(); };
  }, [currentParty?.id]);

  const value = {
    currentParty,
    songQueue,
    currentSong,
    loading,
    error,
    setCurrentParty,
    isPartyOwner
  };

  return (
    <PartyContext.Provider value={value}>
      {children}
    </PartyContext.Provider>
  );
};